import { line, documentStatusPill, fill, formatDate, formatDateOnly, t, type StringKey, type WorkOrderProgressMilestone, type WorkOrderProgressStep, type WorkOrderProgressView } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { ringClass } from './DealProgress';
import deal from './DealPage.module.css';
import styles from './WorkOrderProgress.module.css';

/**
 * 🔒 Job Progress (piece 29; decision 6020; T2787): where the job stands, drawn
 * as Deal Progress draws a Deal's stages, in the same rings, connector and
 * gutter from the Deal page's own stylesheet. Passed steps are ticked and
 * dated, the job's own step lit, and a Void job draws Void where it left the
 * ladder. Beside the steps, the visits, their Service Reports and the invoices.
 * 🔨 Read-only: no act stands on it, and it reads only what the page answer
 * carries, built on the server.
 */
export function WorkOrderProgress({ progress }: { progress: WorkOrderProgressView }) {
  const zone = useViewerZone();
  const format = useDateFormat();
  const instant = (at: string | null) => (at ? formatDate(at, zone, format) : null);
  return (
    <div className={styles.band} data-work-order-progress={progress.status}>
      <section className={styles.column} aria-label={t('workOrder.progress.steps')}>
        <h3 className={styles.heading}>{t('workOrder.progress.steps')}</h3>
        <ol className={deal.ladder}>
          {progress.steps.map((step) => (
            <li key={step.status} className={deal.rung} data-progress-step={step.status} data-progress-step-state={step.state}>
              <span className={`${deal.ring} ${ringClass(stepRing(step))}`} aria-hidden="true">
                {step.state === 'done' ? <Icon meaning="confirmed" size="inline" /> : null}
              </span>
              <span className={stepLabelClass(step)}>{step.status === 'Void' ? t('workOrder.progress.void') : documentStatusPill('WorkOrderStatus', step.status).text}</span>
              {step.at && step.state !== 'todo' ? <span className={styles.date} data-progress-step-at={step.status}>{instant(step.at)}</span> : null}
            </li>
          ))}
        </ol>
      </section>
      <section className={styles.column} aria-label={t('workOrder.progress.milestones')}>
        <h3 className={styles.heading}>{t('workOrder.progress.milestones')}</h3>
        <ul className={deal.subLadder}>
          {progress.milestones.map((milestone) => (
            <li key={`${milestone.kind}-${milestone.key}`} className={deal.subRung} data-progress-milestone={milestone.kind} data-progress-milestone-key={milestone.key} data-progress-milestone-done={String(milestone.done)}>
              <span className={`${deal.ringSmall} ${ringClass(milestone.done ? 'done' : '')}`} aria-hidden="true">
                {milestone.done ? <Icon meaning="confirmed" size="inline" /> : null}
              </span>
              <span className={milestone.done ? deal.subLabel : deal.rungLabel}>{milestoneName(milestone)}</span>
              <span className={styles.date} data-progress-milestone-when={milestone.key}>{milestoneWhen(milestone, instant, (day) => formatDateOnly(day, format))}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

const KIND_KEYS: Readonly<Record<WorkOrderProgressMilestone['kind'], StringKey>> = {
  visit: 'workOrder.progress.kind.visit',
  serviceReport: 'workOrder.progress.kind.serviceReport',
  invoice: 'workOrder.progress.kind.invoice',
};

/** A milestone reads its kind and, once it has one, its own name: *Visit V1*, *Service Report WO-2026-010 · SR1*. */
function milestoneName(milestone: WorkOrderProgressMilestone): string {
  const kind = t(KIND_KEYS[milestone.kind]);
  return milestone.name ? line(kind, milestone.name) : kind;
}

/** When it happened, or that it has not: a visit reads its booked day until it is complete. */
function milestoneWhen(milestone: WorkOrderProgressMilestone, instant: (at: string | null) => string | null, day: (value: string) => string): string {
  const filled = (key: StringKey, date: string) => fill(t(key), { date });
  if (milestone.kind === 'invoice') return milestone.day ? filled('workOrder.progress.raised', day(milestone.day)) : t('workOrder.progress.notYet');
  const at = instant(milestone.at);
  if (milestone.kind === 'visit') {
    if (milestone.key === 'none' || !at) return t('workOrder.progress.notBooked');
    return filled(milestone.done ? 'workOrder.progress.completed' : 'workOrder.progress.booked', at);
  }
  if (!milestone.done) return t('workOrder.progress.notYet');
  return at ? filled('workOrder.progress.signed', at) : t('workOrder.progress.signedUndated');
}

/** The ring a step draws: ticked, lit, the Deal's lost ring for Void, or empty. */
function stepRing(step: WorkOrderProgressStep): string {
  if (step.state === 'void') return 'lost';
  return step.state === 'todo' ? '' : step.state;
}

/** A passed step in the page's ink, the job's own in green, Void in red, a later one muted, as the Deal's ladder reads. */
function stepLabelClass(step: WorkOrderProgressStep): string {
  if (step.state === 'void') return deal.rungLabelStale ?? '';
  if (step.state === 'now') return deal.rungLabelNow ?? '';
  if (step.state === 'done') return deal.rungLabelDone ?? '';
  return deal.rungLabel ?? '';
}
